import { ActionPermissions, CustomPermission, FormConditionCompareOption, FormContainerItem, FormFieldItem, FormFieldType, FormObject, InterceptedFormsObject, ModifyStructureFormModifyStructureType, ParameterDataFormParameterType, ServiceObject } from '../@types';
import { checkForSelectedRules } from './services';
import { PendingInteractionFormType } from '../@types/pendingInteraction.type';
import { DropdownItem } from '../components/atoms/SelectMenu/SelectMenu.props';
import { InteractionRuleTriggerPointType } from '../configs/enums';
import { isUserHasPermission } from '../configs/permissions';
import { ServiceSearchFormData } from '../pages/Services/UpdateService/ServiceActionBarFragment/FormInterceptor/ServiceSearchFormFragment';

export const getTriggerType = (triggerTypeCode: string) => {
  switch (triggerTypeCode) {
  case InteractionRuleTriggerPointType.StatusChange: 
    return 'Status Change';
  case InteractionRuleTriggerPointType.SystemEvent: 
    return 'System Event';
  default:
    return '';
  }
};

export const hasFormFieldValueError = (field: FormFieldItem, service: ServiceObject) => {
  switch (field.type) {
  case FormFieldType.PartsRequest: 
    return !field.value?.partId && !field.value?.partName;
  case FormFieldType.ModifyStructure: 
    return (service.subLocationNodeId && service.subLocationNodeId > 0) || field.preSelectedData?.structureType === ModifyStructureFormModifyStructureType.ToggleActive ? false : (!service.subLocationNodeId && !field.value) || (!service.subLocationNodeId && !field.value?.locationName);
  case FormFieldType.SetParameter:
    return field.preSelectedData?.parameterType === ParameterDataFormParameterType.SetTag ? !field.value?.selectedTags?.length && !field.value?.newTags?.length : !field.value;
  case FormFieldType.ServiceCreation:
    return field.value?.priorityCode < 1 || field.value?.mainLocationNodeId < 1 || checkForSelectedRules(field.value);
  case FormFieldType.GetParameter:
    return !field.value?.parameterValue;
  case FormFieldType.ServiceSearch:
    return field.value.length && !field.value.some((service: ServiceSearchFormData) => service.isSelected);
  case FormFieldType.Checkbox:
  case FormFieldType.GetGeoLocation:
  case FormFieldType.ActionTrigger:
  case FormFieldType.SetParameterNoUI:
    return false;
  default:
    return !field.value;
  }
};

export const getFormIsRepeatableStatus = (selectedform: FormObject, formsList: FormObject[]) => {
  const lastForm = formsList.find((form) => form.id === Math.max(...formsList.filter((form) => form.templateId === selectedform.templateId && form.title === selectedform.title).map((form) => form.id)));
  if (selectedform.isRepeatable && lastForm?.id === selectedform.id) {
    const filteredForms = formsList.filter((form) => form.templateId === selectedform.templateId && form.title === selectedform.title);
    if (selectedform.templateDisplayLimit) {
      if (selectedform.templateDisplayLimit > filteredForms.length + (selectedform.displayCount || 0)) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  } else {
    return false;
  }
};

export const checkFieldIsError = (permissions: CustomPermission[], form: FormObject, container: FormContainerItem, field: FormFieldItem, service: ServiceObject) => {
  if (container.isDecisionPrompt && container.questionData?.hasValue && !container.questionData?.isYes) {
    return false;
  } else if (container.isDecisionPrompt && !container.questionData?.hasValue) {
    return !isUserHasPermission(ActionPermissions.Configure_Interactions_Bypass_Mandatory, permissions) && (field.isRequired || form.isMandatory);
  } else if (field.type === FormFieldType.Label) {
    return false;
  } else if (field.type === FormFieldType.ServiceCreation) {
    return hasFormFieldValueError(field, service);
  } else {
    return !isUserHasPermission(ActionPermissions.Configure_Interactions_Bypass_Mandatory, permissions) && (field.isRequired || form.isMandatory) && hasFormFieldValueError(field, service);
  }
};

export const checkIsByPassed = (permissions: CustomPermission[], form: FormObject, container: FormContainerItem, field: FormFieldItem, service: ServiceObject) => {
  if (container.isDecisionPrompt && container.questionData?.hasValue && !container.questionData?.isYes) {
    return false;
  } else if (isUserHasPermission(ActionPermissions.Configure_Interactions_Bypass_Mandatory, permissions) && form.isMandatory && container.isDecisionPrompt && !container.questionData?.hasValue) {
    return true;
  } else if (field.type === FormFieldType.Label || field.type === FormFieldType.ServiceCreation) {
    return false;
  } else {
    return isUserHasPermission(ActionPermissions.Configure_Interactions_Bypass_Mandatory, permissions) && (field.isRequired || form.isMandatory) && hasFormFieldValueError(field, service);
  }
};

export const getSortedPendingInteractions = (formsWithDisplayOrder: PendingInteractionFormType[], formsWithoutDisplayOrder: PendingInteractionFormType[]) => {
  return formsWithDisplayOrder.sort((form1, form2) => (form1.displayOrder as number) - (form2.displayOrder as number)).concat(formsWithoutDisplayOrder);
};

export const filterInteractionFormGeneration = (field: FormFieldItem) => {
  switch (field.type) {
  case FormFieldType.ModifyStructure: 
    return field.preSelectedData?.structureType !== ModifyStructureFormModifyStructureType.ToggleActive;
  default:
    return true;
  }
};

export const blockInteractionAvailable = (permissions: CustomPermission[], interactionForms: FormObject[]) => {
  return !isUserHasPermission(ActionPermissions.Configure_Interactions_Bypass_Mandatory, permissions) &&
    interactionForms.some((form) => form.containers.some((container) => container.fields.some((field) => field.type === FormFieldType.Block)));
};

export const getBlockInteractionMessage= (interactionForms: FormObject[]) => {
  for (const form of interactionForms) {
    for (const container of form.containers) {
      for (const field of container.fields) {
        if (field.type === FormFieldType.Block) {
          return field?.blockMessage || '';
        }
      }
    }
  }
};

export const validateInteractionForms = (interceptedFormsObject: InterceptedFormsObject, service: ServiceObject, permissions: CustomPermission[]) => {
  let hasErrorFields = false;
  let hasByPassedFields = false;
  let blockInteractionBypassed = false;
  const updatedForms: FormObject[] = [];

  // isMandatory & isRequired validation
  interceptedFormsObject?.forms.forEach((form) => {
    const byPassedFormFields: string[] = [];
    const updatedContainers: FormContainerItem[] = [];
    form.containers.forEach((container) => {
      const updatedFields: FormFieldItem[] = [];
      container.fields.forEach((field) => {
        const updatedFieldObject = {
          ...field,
          isError: checkFieldIsError(permissions || [], form, container, field, service)
        };
        hasErrorFields = updatedFieldObject.isError || hasErrorFields;
        updatedFields.push(updatedFieldObject);
        if (checkIsByPassed(permissions || [], form, container, field, service)) {
          byPassedFormFields.push(field.title || field.type);
          hasByPassedFields = true;
        }
        if (isUserHasPermission(ActionPermissions.Configure_Interactions_Bypass_Mandatory, permissions) && field.type === FormFieldType.Block && !blockInteractionBypassed) {
          blockInteractionBypassed = true;
        }
      });
      updatedContainers.push({ ...container, fields: updatedFields });
    });
    updatedForms.push({ ...form, containers: updatedContainers, byPassedFormFields, blockInteractionBypassed });
  });

  return {
    hasErrorFields,
    hasByPassedFields,
    blockInteractionBypassed,
    updatedForms
  };
};

export const hasByPassedFields = (interceptedFormsObject: InterceptedFormsObject, service: ServiceObject, permissions: CustomPermission[]) => {
  if (!interceptedFormsObject) return true;
  if (isUserHasPermission(ActionPermissions.Configure_Interactions_Bypass_Mandatory, permissions)) return true;

  for (const form of interceptedFormsObject.forms) {
    for (const container of form.containers) {
      for (const field of container.fields) {
        if (checkFieldIsError(permissions || [], form, container, field, service)) return false;
      }
    }
  }

  return true;
};

export const updateErrorState = (interceptedFormsObject: InterceptedFormsObject, form: FormObject, container: FormContainerItem, field: FormFieldItem) => {
  if (interceptedFormsObject) {
    const updatedFields: Array<FormFieldItem> = container.fields.map((obj) => obj.id === field.id ? {
      ...field,
      isError: true
    } : obj);

    const updatedContainers: Array<FormContainerItem> = form.containers.map((obj) => obj.id === container.id ? {
      ...container,
      fields: updatedFields
    } : obj);

    const updatedForms = interceptedFormsObject.forms.map((obj) => obj.id === form.id ? {
      ...form,
      containers: updatedContainers
    } : obj);

    return updatedForms;
  }
};

export const getExtraSubConditionalConditions = (containers: FormContainerItem[], currentContainerIndex: number) => {
  const extraSubConditionalConditions: DropdownItem[] = [];
  for (const [index, container] of containers.entries()) {
    for (const field of container.fields) {
      if (
        [FormFieldType.Dropdown, FormFieldType.Radio, FormFieldType.Checkbox].includes(field.type) &&
        index < currentContainerIndex
      ) {
        extraSubConditionalConditions.push({
          label: field.title,
          value: field.title
        });
      }
    }

    // Checking available conditions from previous sub-conditions
    if (container.subConditions && container.subConditions?.length > 0) {
      for (const subCondition of container.subConditions) {
        // Then container
        if (subCondition.thenContainer.fields && subCondition.thenContainer.fields?.length > 0) {
          for (const subConditionField of subCondition.thenContainer.fields) {
            if (
              [FormFieldType.Dropdown, FormFieldType.Radio, FormFieldType.Checkbox].includes(subConditionField.type) &&
             index < currentContainerIndex
            ) {
              extraSubConditionalConditions.push({
                label: subConditionField.title,
                value: subConditionField.title
              });
            }
          }
        }
        
        //Else container
        if (subCondition.elseContainer.fields && subCondition.elseContainer.fields?.length > 0) {
          for (const subConditionField of subCondition.elseContainer.fields) {
            if (
              [FormFieldType.Dropdown, FormFieldType.Radio, FormFieldType.Checkbox].includes(subConditionField.type) &&
             index < currentContainerIndex
            ) {
              extraSubConditionalConditions.push({
                label: subConditionField.title,
                value: subConditionField.title
              });
            }
          }
        }
      }
    }

  }
  return extraSubConditionalConditions;
};

export const getSubConditionalConditions = (subConditionalConditions: DropdownItem[], extraSubConditionalConditions?: DropdownItem[]) => {
  return extraSubConditionalConditions && extraSubConditionalConditions?.length > 0 ? subConditionalConditions.concat(extraSubConditionalConditions) : subConditionalConditions;
};

export const compareValuesByCompareOption = (compareOption: string, compareValue1: any, compareValue2: any) => {
  switch (compareOption) {
  case FormConditionCompareOption.Equal: {
    return compareValue1?.toLocaleLowerCase() === compareValue2?.toLocaleLowerCase();
  }
  case FormConditionCompareOption.Wildcard: {
    const haystack = (compareValue1 ?? '').toLocaleLowerCase();
    const pattern = (compareValue2 ?? '').toLocaleLowerCase();
    if (!pattern.includes('%')) return haystack === pattern;
    const tokens = pattern.split('%');
    const startsWithWild = pattern.startsWith('%');
    const endsWithWild = pattern.endsWith('%');
    let pos = 0;
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];
      if (token === '') continue;
      if (i === 0 && !startsWithWild) {
        if (!haystack.startsWith(token)) return false;
        pos = token.length;
      } else if (i === tokens.length - 1 && !endsWithWild) {
        return haystack.endsWith(token) && haystack.length - token.length >= pos;
      } else {
        const idx = haystack.indexOf(token, pos);
        if (idx === -1) return false;
        pos = idx + token.length;
      }
    }
    return true;
  }
  default:
    return false;
  }
};